// /reports/cancellation-trends — Cancellation Trends (PAGES.md §3.92) — RESTAURANT

import { getSubunitMultiplier } from '@bbpos/utils';
import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';
import type { CancellationByItemRow, CancellationByReasonRow, CancellationTrendRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { formatPaise } from '@/utils/currency';
import type { CurrencyCode } from '@bbpos/types';

type ItemRow = CancellationByItemRow & { id: string };
type ReasonRow = CancellationByReasonRow & { id: string };
type TrendRow = CancellationTrendRow & { id: string };

const itemCols: ColumnDef<ItemRow>[] = [
  { key: 'product_name', header: 'Item', render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.product_name}</span> },
  { key: 'cancellation_count', header: 'Cancelled', numeric: true, render: (_v, row) => <span className="text-sm font-semibold text-danger-600 tabular-nums">{row.cancellation_count}</span> },
];

const makeReasonCols = (currency: CurrencyCode): ColumnDef<ReasonRow>[] => [
  { key: 'reason', header: 'Reason', render: (_v, row) => <span className="text-sm text-neutral-900 dark:text-neutral-100">{row.reason}</span> },
  { key: 'count', header: 'Count', numeric: true, render: (_v, row) => <span className="tabular-nums font-semibold text-danger-600">{row.count}</span> },
  { key: 'amount', header: 'Lost Revenue', numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.amount, currency)}</span> },
];

const trendCols: ColumnDef<TrendRow>[] = [
  { key: 'date', header: 'Date', render: (_v, row) => <span className="text-sm tabular-nums">{row.date}</span> },
  { key: 'cancellations', header: 'Cancellations', numeric: true, render: (_v, row) => <span className="tabular-nums font-semibold text-danger-600">{row.cancellations}</span> },
  { key: 'rate_pct', header: 'Rate %', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.rate_pct.toFixed(1)}%</span> },
];

export function CancellationTrendsReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const reasonCols = useMemo(() => makeReasonCols(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'cancellation-trends',
    filter,
    queryFn: (f) => reportsApi.getCancellationTrends(f),
  });

  const items: ItemRow[] = useMemo(
    () => (data?.by_item ?? []).map((r, i) => ({ ...r, id: String(i) })),
    [data],
  );
  const reasons: ReasonRow[] = useMemo(
    () => (data?.by_reason ?? []).map((r, i) => ({ ...r, id: String(i) })),
    [data],
  );
  const trend: TrendRow[] = useMemo(
    () => (data?.trend ?? []).map((r) => ({ ...r, id: r.date })),
    [data],
  );

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `cancellation-trends-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Item', get: (r: ItemRow) => r.product_name },
          { header: 'Cancelled', get: (r: ItemRow) => r.cancellation_count },
        ],
        items,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(
        fmt,
        'Orio360 Cancellation Trends',
        `Cancel rate: ${(data?.cancellation_rate_pct ?? 0).toFixed(1)}% • ${data?.total_cancellations ?? 0} items`,
      );
    }
  };

  return (
    <ReportPageShell
      title="Cancellation Trends"
      subtitle="Cancel rate, top cancelled items, reasons and daily trend."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Cancellation Trends' }]}
      accent="warning-danger"
      headerActions={<ReportExportMenu onExport={handleExport} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={3} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'rate', title: 'Cancel Rate', value: `${(data?.cancellation_rate_pct ?? 0).toFixed(1)}%`, tone: 'danger', icon: KpiIcons.percent },
              { id: 'total', title: 'Total Cancelled', value: (data?.total_cancellations ?? 0).toLocaleString('en-IN'), tone: 'warning', icon: KpiIcons.trendDown },
              { id: 'revenue', title: 'Lost Revenue', value: `${symbol}${((data?.total_lost_revenue ?? 0) / getSubunitMultiplier(currency)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, tone: 'pink', icon: KpiIcons.rupee },
            ]}
          />
          <ReportTable title="Top Cancelled Items" columns={itemCols} data={items} emptyTitle="No cancellation data for selected period" totalCount={items.length} />
          <ReportTable title="Cancellations by Reason" columns={reasonCols} data={reasons} emptyTitle="No reason data for selected period" totalCount={reasons.length} />
          <ReportTable title="Daily Cancellation Trend" columns={trendCols} data={trend} emptyTitle="No trend data for selected period" totalCount={trend.length} />
        </>
      )}
    </ReportPageShell>
  );
}
